<script>
    import { Row, Col, Alert } from 'sveltestrap'
    import { Select, InputColorPicker } from '@fno/component2/form.js'
    import FormAlerts from '../FormAlerts.svelte'
    import FormSavingButtons from '../FormSavingButtons.svelte'
    import FormImageUpload from '@fno/component2/form/InputFile.svelte'
    import { createEventDispatcher } from 'svelte'
    import FormColorPicker from '@fno/component2/form/InputColor.svelte'
    import { postPublicFile } from '@fno/service/api/files'
    import Icon from 'svelte-awesome'
    import { arrowRight, arrowLeft } from 'svelte-awesome/icons'

    import HomePreview from './previews/home/home-preview.svelte'
    import EventPreview from './previews/event-info/event-preview.svelte'
    import SchedulePreview from './previews/schedule/schedule-preview.svelte'
    import TeamPreview from './previews/team-info/team-info-preview.svelte'

    export let context
    export let saving

    const staticAssetPrefix = (window.fanxVersion && window.fanxVersion.urlPrefix) || '/'

    let primaryColor
    let secondaryColor
    let tertiaryColor
    let accentColor
    let rowIconColor
    let tabBarHighlightTextColor
    let homeRoundiesColor

    // control whether or not we need to compose the icon from logo and bgcolor
    $: composeIcon = !context.form.clientOnboarding.attributes.steps.APP_COLORS.launchIcon.key
    $: previewColor = `#${context.form.clientOnboarding.attributes.steps.APP_COLORS.backgroundColor}`
    $: previewLogo =
        context.form.clientOnboarding.attributes.steps.APP_COLORS.launchIconLogo.key &&
        `https://${context.form.clientOnboarding.attributes.steps.APP_COLORS.launchIconLogo.bucket}/${context.form.clientOnboarding.attributes.steps.APP_COLORS.launchIconLogo.key}`
    $: launchIcon =
        context.form.clientOnboarding.attributes.steps.APP_COLORS.launchIcon.key &&
        `https://${context.form.clientOnboarding.attributes.steps.APP_COLORS.launchIcon.bucket}/${context.form.clientOnboarding.attributes.steps.APP_COLORS.launchIcon.key}`

    const dispatch = createEventDispatcher()
    const updatePreviewColor = (event) => {
        previewColor = `#${event.detail.value}`
        dispatch('change', event.detail)
    }

    let currentSlide = 0
    const maxSlide = 3
    const moveSlides = (direction) => {
        // check if current slide is the last and reset current slide
        if ( (currentSlide === maxSlide || currentSlide < 0) && direction !== -1) {
            currentSlide = 0;
        } else {
            if (direction === 1) {
                currentSlide++;
            } else {
                if (currentSlide > 0) {
                    currentSlide--;
                }
            }
        }

        const slides = document.querySelectorAll('.slide');
        slides.forEach((slide, index) => {
            // index = 0, 1, 2, direction = 1 or -1
            slide.style.transform = `translateX(${100 * (index - currentSlide)}%)`;
        });
    }

    $: primaryColor = `#${context.form.appColors.attributes.primary}`
    $: secondaryColor = `#${context.form.appColors.attributes.secondary}`
    $: tertiaryColor = `#${context.form.appColors.attributes.tertiary}`
    $: accentColor = `#${context.form.appColors.attributes.accent}`
    $: rowIconColor = `#${context.form.appColors.attributes.rowIcon}`
    $: tabBarHighlightTextColor = `#${context.form.appColors.attributes.tabBarHighlightText}`
    $: homeRoundiesColor = `#${context.form.appColors.attributes.homeRoundies}`

    export let finishedIcon = context.form.clientOnboarding.attributes.steps.APP_COLORS.launchIcon.filename

    const themeOptions = [
        {
            id: false,
            name: 'Light Theme'
        },
        {
            id: true,
            name: 'Dark Theme'
        }
    ]

    let scrollY
    let affix

    $: {
        if (affix) {
            if (scrollY > 214) {
                affix.style.top = '0'
                affix.style.left = `${affix.offsetLeft}px`
                affix.style.position = 'sticky'
            } else {
                affix.style.position = 'inherit'
                affix.style.top = 'inherit'
                affix.style.left = 'inherit'
            }
        }
    }
</script>

<style>
    .fno-launch-icon-preview {
        width: 150px;
        height: 150px;
    }

    .fno-launch-icon {
        width: 112px;
        height: 112px;
        object-fit: scale-down;
    }

    .ios {
        border-radius: 17.5%;
    }

    .android {
        border-radius: 50%;
    }

    .fno-color-pickers :global(.component-form-field-color) {
        margin: 25px 0;
    }

    .slider {
        width: 100%;
        max-width: 414px;
        height: 896px;
        position: relative;
        overflow: hidden;
    }

    .slide {
        width: 100%;
        max-width: 800px;
        height: 350px;
        position: absolute;
        transition: all 0.5s;
    }

    .slider-navigation {
        width: 100%;
        display: flex;
        flex-direction: row;
        justify-content: space-evenly;
        align-items: center;
        margin-bottom: 10px;
    }

    .btn {
        width: 40px;
        height: 40px;
        border: none;
        border-radius: 50%;
        cursor: pointer;
        background-color: #fff;
        font-size: 18px;
    }

    .btn:active {
        transform: scale(1.1);
    }

    .btn-prev {
        top: 0;
        left: 2%;
    }

    .btn-next {
        top: 0;
        right: 2%;
    }
    .input-info-circle{
        position: relative;
        width: fit-content;
    }
</style>

<svelte:window bind:scrollY />

<h1>
    Design Your App
</h1>

<h2>
    App Icon
</h2>

<p>
    The App Icon is the tappable image that displays on your device's home screen
    amongst the other apps you have downloaded. For a simple app icon, upload a branded
    logo in the <em>App Icon Logo</em> box. Select a color or enter a HEX color code
    to select a solid color background. If you would like to use a custom-designed App
    Icon, select the <em>I’d like to use a custom App Icon</em> option below.
</p>

<Row class='mt-5'>
    <Col>
        <Row>
            <Col>
                <FormImageUpload
                    label="App Icon Logo"
                    helptext="Transparent icon to be used with a background color to generate a launch icon. Recommended file type and size: 512 x 512px, .png"
                    {context}
                    formKey="clientOnboarding"
                    keypath="attributes.steps.APP_COLORS.launchIconLogo"
                    bind:value={context.form.clientOnboarding.attributes.steps.APP_COLORS.launchIconLogo}
                    fileUploader={postPublicFile}
                    on:change
                />
            </Col>
        </Row>

        <Row>
            <Col class="d-flex justify-content-center">
                <FormColorPicker
                    {context}
                    formKey="clientOnboarding"
                    keypath="attributes.steps.APP_COLORS.backgroundColor"
                    on:change={(event) => updatePreviewColor(event)}
                    bind:value={context.form.clientOnboarding.attributes.steps.APP_COLORS.backgroundColor}
                />
            </Col>
        </Row>
    </Col>

    <Col class="d-flex flex-column align-items-center">
        <h4>Previews</h4>

        {#if composeIcon}
            <h5>iOS</h5>
            <div
                class="fno-launch-icon-preview ios d-flex justify-content-center align-items-center"
                style={`background-color: ${previewColor};`}
            >
                {#if previewLogo}
                    <img class="fno-launch-icon" src={previewLogo} alt="iOS preview logo on background color" />
                {/if}
            </div>
            <br>
            <h5>Android</h5>
            <div
                    class="fno-launch-icon-preview android d-flex justify-content-center align-items-center"
                    style={`background-color: ${previewColor};`}
            >
                {#if previewLogo}
                    <img class="fno-launch-icon" src={previewLogo} alt="Android preview logo on background color" />
                {/if}
            </div>
        {:else}
            <h5>iOS</h5>
            <img class="fno-launch-icon-preview ios" src={launchIcon} alt="pre-finished launch icon" />
            <br>
            <h5>Android</h5>
            <img class="fno-launch-icon-preview android" src={launchIcon} alt="pre-finished launch icon" />
        {/if}
    </Col>
</Row>

<Row class='mt-5'>
    <Col xs="6">
        <Alert color='primary'>
            <div on:focus class="input-info-circle">
                <input type="checkbox" bind:checked={ finishedIcon } class="fnoex-list-item-selection me-2">
                I’d like to use a custom App Icon
            </div>
        </Alert>

        {#if finishedIcon}
            <FormImageUpload
                label="Launch Icon"
                helptext="Finished launch icon to be used, as-is. Recommended file type and size: 1024 x 1024 pixels, .png"
                {context}
                formKey="clientOnboarding"
                keypath="attributes.steps.APP_COLORS.launchIcon"
                bind:value={context.form.clientOnboarding.attributes.steps.APP_COLORS.launchIcon}
                fileUploader={postPublicFile}
                on:change
            />

            <p class='my-3'>
                <a href='{staticAssetPrefix}img/onboarding/app-launch-icon-template.psd' download>APP ICON PHOTOSHOP TEMPLATE</a>
            </p>

            <div class='d-flex flex-direction-row justify-content-around align-items-center'>
                <span>Examples:</span>
                <img src='{staticAssetPrefix}img/onboarding/example-icon-1.png' alt='example app icon #1' />
                <img src='{staticAssetPrefix}img/onboarding/example-icon-2.png' alt='example app icon #2' />
                <img src='{staticAssetPrefix}img/onboarding/example-icon-3.png' alt='example app icon #3' />
            </div>
        {/if}
    </Col>
</Row>

<h2 class="mt-5">
    Colors
</h2>

<p>
    Using the tools below, you can begin designing the look and feel of your app.
    The screenshots on the right will adjust to display any changes made within the
    light or dark theme dropdown and the color assignments below. Be sure to use
    your official HEX codes for accurate brand representation.
</p>

<p>
    NOTE: Your selections will be reviewed for ADA Color compliance prior to submission of the app.
</p>

<Row class="mt-5">
    <Col xs="12" md="12" lg="4" xl="4">
        <div bind:this={affix}>
            <Select
                label="Dark or Light Theme"
                {context}
                slim={true}
                options={themeOptions}
                formKey="appColors"
                keypath="attributes.dark"
                bind:value={context.form.appColors.attributes.dark}
                on:change
            />

            <div class="fno-color-pickers d-flex flex-column align-items-center">
                <InputColorPicker
                    {context}
                    required
                    slim={true}
                    label="Primary Color (Hex #)"
                    formKey="appColors"
                    keypath="attributes.primary"
                    on:change
                    defaultValue={context.view.appColorsSchema.properties.primary.default}
                    bind:value={context.form.appColors.attributes.primary}
                />

                <InputColorPicker
                    {context}
                    required
                    slim={true}
                    label="Secondary Color (Hex #)"
                    formKey="appColors"
                    keypath="attributes.secondary"
                    on:change
                    bind:value={context.form.appColors.attributes.secondary}
                />

                <InputColorPicker
                    {context}
                    required
                    slim={true}
                    label="Tertiary Color (Hex #)"
                    formKey="appColors"
                    keypath="attributes.tertiary"
                    on:change
                    bind:value={context.form.appColors.attributes.tertiary}
                />

                <InputColorPicker
                    {context}
                    required
                    slim={true}
                    label="Home Roundies Color (Hex #)"
                    formKey="appColors"
                    keypath="attributes.homeRoundies"
                    on:change
                    bind:value={context.form.appColors.attributes.homeRoundies}
                />

                <InputColorPicker
                    {context}
                    required
                    slim={true}
                    label="Accent Color (Hex #)"
                    formKey="appColors"
                    keypath="attributes.accent"
                    on:change
                    bind:value={context.form.appColors.attributes.accent}
                />

                <InputColorPicker
                    {context}
                    required
                    slim={true}
                    label="Row Icon Color (Hex #)"
                    formKey="appColors"
                    keypath="attributes.rowIcon"
                    on:change
                    bind:value={context.form.appColors.attributes.rowIcon}
                />

                <InputColorPicker
                    {context}
                    required
                    slim={true}
                    label="Tab Bar Text Color (Hex #)"
                    formKey="appColors"
                    keypath="attributes.tabBarHighlightText"
                    on:change
                    bind:value={context.form.appColors.attributes.tabBarHighlightText}
                />

            </div>
        </div>
    </Col>
    <Col xs="12" md="12" lg="8" xl="8" class="d-flex flex-column align-items-center">
        <h2 class='mt-5'>Preview</h2>

        <div class="slider-navigation">
            <button class="btn btn-prev" on:click={ () => moveSlides(-1) }> <Icon data={arrowLeft} /> </button>
            <span>{ currentSlide + 1 } of 4</span>
            <button class="btn btn-next" on:click={ () => moveSlides(1) }> <Icon data={arrowRight} /> </button>
        </div>

        <div class="slider">
            <!-- SCREEN ONE: HOME -->
            <div class="slide" style="transform: translateX(0%);">
                <HomePreview
                    theme={context.form.appColors.attributes.dark}
                    primary={`${primaryColor}`}
                    secondary={`${secondaryColor}`}
                    tertiary={`${tertiaryColor}`}
                    accentColor={`${accentColor}`}
                    rowIconColor={`${rowIconColor}`}
                    tabBarHighlightTextColor={`${tabBarHighlightTextColor}`}
                    homeRoundiesColor={`${homeRoundiesColor}`}
                />
            </div>

            <!-- SCREEN TWO: EVENT -->
            <div class="slide" style="transform: translateX(100%);">
                <EventPreview
                    theme={context.form.appColors.attributes.dark}
                    primary={`${primaryColor}`}
                    secondary={`${secondaryColor}`}
                    tertiary={`${tertiaryColor}`}
                    rowIconColor={`${rowIconColor}`}
                    tabBarHighlightTextColor={`${tabBarHighlightTextColor}`}
                />
            </div>

            <!-- SCREEN THREE: SCHEDULE -->
            <div class="slide" style="transform: translateX(200%);">
                <SchedulePreview
                    theme={context.form.appColors.attributes.dark}
                    primary={`${primaryColor}`}
                    secondary={`${secondaryColor}`}
                    accentColor={`${accentColor}`}
                    rowIconColor={`${rowIconColor}`}
                    tabBarHighlightTextColor={`${tabBarHighlightTextColor}`}
                />
            </div>

            <!-- SCREEN FOUR: TEAM  -->
            <div class="slide" style="transform: translateX(300%);">
                <TeamPreview
                    theme={context.form.appColors.attributes.dark}
                    primary={`${primaryColor}`}
                    secondary={`${secondaryColor}`}
                    tertiary={`${tertiaryColor}`}
                    rowIconColor={`${rowIconColor}`}
                    tabBarHighlightTextColor={`${tabBarHighlightTextColor}`}
                />
            </div>
        </div>

    </Col>
</Row>

<FormAlerts bind:alerts={context.alerts} />

<FormSavingButtons {context} {saving} on:save on:nextStepClicked on:previousStepClicked />
